import { hasSeqGap } from "@cookies/shared/protocol";
import { getToken, resolveGuildId } from "../../auth/session";
import type { ClientEvent, GameConnection, ServerEvent } from "../types";
import { normalizeCosmetics, normalizeEvent, normalizePlayer, normalizeSnapshot, num, str } from "./normalize";
import { WS_CONNECT_TIMEOUT_MS, WS_HEARTBEAT_MS, WS_MAX_BACKOFF_MS, WS_BASE_BACKOFF_MS, WS_RESYNC_THROTTLE_MS, wsOrigin } from "./wsConfig";
import { WS_OUTBOX_CLICK_CAP, WS_OUTBOX_MAX } from "@cookies/shared/config";
import { handleAbilityActivated, handleCosmeticEquipped, handleCosmeticPurchased, handleModuleUpgraded, handleProjectCompleted, handleProjectStarted, handleQuestClaimed, handleUpgradePurchased } from "./eventHandlers";
import type { ConnectionOptions } from "./wsConfig";

export type { ConnectionOptions } from "./wsConfig";

export class WebsiteGameConnection implements GameConnection {
  private ws: WebSocket | null = null;
  private handlers = new Set<(e: ServerEvent) => void>();
  private closed = false;
  private lastSeq = 0;
  private retries = 0;
  private timer: number | null = null;
  private heartbeat: number | null = null;
  private userId: string;
  private guildId: string;
  private token: string | null;
  private connectInflight: Promise<void> | null = null;
  // Fire-and-forget outbox: sends issued while the socket is not OPEN are
  // queued here (instead of dropped) and replayed on reconnect. Buys are
  // therefore never lost on a reconnect race; the server dedupes retries by
  // requestId and the client heals from ack/broadcast/snapshot.
  private outbox: ClientEvent[] = [];

  constructor(opts?: ConnectionOptions) {
    this.userId = opts?.userId ?? "";
    this.guildId = opts?.guildId ?? resolveGuildId() ?? "";
    this.token = opts?.token ?? getToken();
  }

  onEvent(h: (e: ServerEvent) => void): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  isLive(): boolean {
    try {
      return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    } catch {
      return false;
    }
  }

  private emit(e: ServerEvent) {
    this.handlers.forEach((h) => h(e));
  }

  connect(): Promise<void> {
    if (this.connectInflight) return this.connectInflight;
    if (this.isLive()) return Promise.resolve();
    this.closed = false;
    this.retries = 0;
    const task = new Promise<void>((resolve, reject) => {
      let settled = false;
      const failTimer = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          try {
            this.ws?.close();
          } catch {
            return;
          }
          reject(new Error("ws-timeout"));
        }
      }, WS_CONNECT_TIMEOUT_MS);
      this.open(
        () => {
          if (!settled) {
            settled = true;
            window.clearTimeout(failTimer);
            resolve();
          }
        },
        (err) => {
          if (!settled) {
            settled = true;
            window.clearTimeout(failTimer);
            reject(err);
          }
        },
      );
    });
    this.connectInflight = task;
    const clearInflight = () => {
      if (this.connectInflight === task) this.connectInflight = null;
    };
    task.then(clearInflight, clearInflight);
    return task;
  }

  private helloToken(): string {
    return this.token ?? "";
  }

  private hello(): ClientEvent {
    return {
      type: "hello",
      userId: this.userId,
      guildId: this.guildId,
      token: this.helloToken(),
      lastSeq: this.lastSeq > 0 ? this.lastSeq : undefined,
    };
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeat = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: "heartbeat" }));
        } catch {
          return;
        }
      }
    }, WS_HEARTBEAT_MS);
  }

  private stopHeartbeat() {
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  private open(onFirstOpen: () => void, onFirstError: (e: unknown) => void) {
    if (this.closed) return;
    const url =
      wsOrigin() +
      "/ws?guildId=" +
      encodeURIComponent(this.guildId) +
      "&token=" +
      encodeURIComponent(this.helloToken());
    let sock: WebSocket;
    try {
      sock = new WebSocket(url);
    } catch (e) {
      onFirstError(e);
      this.schedule();
      return;
    }
    this.ws = sock;
    let opened = false;
    sock.onopen = () => {
      opened = true;
      this.retries = 0;
      this.emit({ type: "status", connected: true });
      this.send(this.hello());
      this.flushOutbox();
      this.startHeartbeat();
      onFirstOpen();
    };
    sock.onmessage = (msg) => {
      try {
        const data = JSON.parse(String(msg.data));
        this.route(data);
      } catch {
        return;
      }
    };
    sock.onerror = () => {
      if (!opened) onFirstError(new Error("ws-error"));
    };
    sock.onclose = (ev) => {
      this.stopHeartbeat();
      this.emit({ type: "status", connected: false });
      const code = typeof ev.code === "number" ? ev.code : 0;
      const reason = typeof ev.reason === "string" ? ev.reason : "";
      if (code === 4403 || code === 4401 || reason === "forbidden" || reason === "unauthorized") {
        this.emit({ type: "error", message: reason !== "" ? reason : String(code) });
      }
      if (!opened) {
        try {
          onFirstError(new Error("ws-closed"));
        } catch {
          return;
        }
      }
      this.schedule();
    };
  }

  private schedule() {
    if (this.closed) return;
    this.retries += 1;
    const wait = Math.min(WS_BASE_BACKOFF_MS * (1 << Math.min(this.retries - 1, 3)), WS_MAX_BACKOFF_MS);
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.open(
        () => undefined,
        () => undefined,
      );
    }, wait);
  }

  private route(data: unknown) {
    if (!data || typeof data !== "object") return;
    const d = data as Record<string, unknown>;
    if (d["snapshot"] && typeof d["snapshot"] === "object") {
      const snap = normalizeSnapshot(d["snapshot"] as Record<string, unknown>);
      if (snap) {
        this.lastSeq = snap.seq;
        this.emit({ type: "snapshot", snapshot: snap });
      }
      return;
    }
    const t = d["type"];
    if (t === "heartbeat_ack") return;
    if (t === "seq_ack") {
      const seq = num(d["seq"], 0);
      if (seq > 0) this.lastSeq = Math.max(this.lastSeq, seq);
      return;
    }
    if (t === "ack") {
      // Acks are per-request confirmations, not ordered stream events: never
      // drop them on seq logic (a deduplicated retry ack carries a stale seq
      // but still confirms the purchase). Advance the high-water mark and
      // always emit; only explicit ok:false nacks surface an error toast.
      const aseq = num(d["seq"], 0);
      if (aseq > 0) this.lastSeq = Math.max(this.lastSeq, aseq);
      this.emit({
        type: "ack",
        requestId: typeof d["requestId"] === "string" ? (d["requestId"] as string) : "",
        ok: d["ok"] === true,
        reason: typeof d["reason"] === "string" ? (d["reason"] as string) : typeof d["message"] === "string" ? (d["message"] as string) : "",
        seq: num(d["seq"], this.lastSeq),
        userId: typeof d["userId"] === "string" ? (d["userId"] as string) : "",
        upgradeId: typeof d["upgradeId"] === "string" ? (d["upgradeId"] as string) : undefined,
        moduleId: typeof d["moduleId"] === "string" ? (d["moduleId"] as string) : undefined,
        projectId: typeof d["projectId"] === "string" ? (d["projectId"] as string) : undefined,
        level: num(d["level"], 0),
        cost: typeof d["cost"] === "string" ? (d["cost"] as string) : undefined,
        credits: typeof d["credits"] === "string" ? (d["credits"] as string) : undefined,
        cookies: typeof d["cookies"] === "string" ? (d["cookies"] as string) : undefined,
        clickPower: typeof d["clickPower"] === "string" ? (d["clickPower"] as string) : undefined,
        cps: typeof d["cps"] === "string" ? (d["cps"] as string) : undefined,
        cpsBonus: typeof d["cpsBonus"] === "string" ? (d["cpsBonus"] as string) : undefined,
        specId: typeof d["specId"] === "string" ? (d["specId"] as string) : undefined,
        questId: typeof d["questId"] === "string" ? (d["questId"] as string) : undefined,
        abilityId: typeof d["abilityId"] === "string" ? (d["abilityId"] as string) : undefined,
        cosmeticId: typeof d["cosmeticId"] === "string" ? (d["cosmeticId"] as string) : undefined,
        slot: typeof d["slot"] === "string" ? (d["slot"] as string) : undefined,
        currency: typeof d["currency"] === "string" ? (d["currency"] as string) : undefined,
        startedAt: typeof d["startedAt"] === "number" && Number.isFinite(d["startedAt"] as number) ? (d["startedAt"] as number) : undefined,
        endsAt: typeof d["endsAt"] === "number" && Number.isFinite(d["endsAt"] as number) ? (d["endsAt"] as number) : undefined,
        durationMs: typeof d["durationMs"] === "number" && Number.isFinite(d["durationMs"] as number) ? (d["durationMs"] as number) : undefined,
        cooldownMs: typeof d["cooldownMs"] === "number" && Number.isFinite(d["cooldownMs"] as number) ? (d["cooldownMs"] as number) : undefined,
        cooldownRemainingMs: typeof d["cooldownRemainingMs"] === "number" && Number.isFinite(d["cooldownRemainingMs"] as number) ? (d["cooldownRemainingMs"] as number) : undefined,
        bakeryCookies: typeof d["bakeryCookies"] === "string" ? (d["bakeryCookies"] as string) : undefined,
        bakeryCps: typeof d["bakeryCps"] === "string" ? (d["bakeryCps"] as string) : undefined,
        bakeryLifetimeCookies: typeof d["bakeryLifetimeCookies"] === "string" ? (d["bakeryLifetimeCookies"] as string) : undefined,
        bakeryLevel: typeof d["bakeryLevel"] === "number" && Number.isFinite(d["bakeryLevel"] as number) ? (d["bakeryLevel"] as number) : undefined,
        bakeryNextLevelCookies: typeof d["bakeryNextLevelCookies"] === "string" ? (d["bakeryNextLevelCookies"] as string) : undefined,
        bakeryLevelProgress: typeof d["bakeryLevelProgress"] === "number" && Number.isFinite(d["bakeryLevelProgress"] as number) ? (d["bakeryLevelProgress"] as number) : undefined,
      } as unknown as never);
      const reason = typeof d["reason"] === "string" && d["reason"] !== "" ? (d["reason"] as string) : "";
      if (d["ok"] !== true && reason !== "") {
        this.emit({
          type: "error",
          message: reason,
          requestId: typeof d["requestId"] === "string" ? (d["requestId"] as string) : undefined,
          reason,
          abilityId: typeof d["abilityId"] === "string" ? (d["abilityId"] as string) : undefined,
          upgradeId: typeof d["upgradeId"] === "string" ? (d["upgradeId"] as string) : undefined,
          moduleId: typeof d["moduleId"] === "string" ? (d["moduleId"] as string) : undefined,
          projectId: typeof d["projectId"] === "string" ? (d["projectId"] as string) : undefined,
          questId: typeof d["questId"] === "string" ? (d["questId"] as string) : undefined,
          level: num(d["level"], 0),
          cost: typeof d["cost"] === "string" ? (d["cost"] as string) : undefined,
          need: typeof d["need"] === "string" ? (d["need"] as string) : undefined,
          have: typeof d["have"] === "string" ? (d["have"] as string) : undefined,
          credits: typeof d["credits"] === "string" ? (d["credits"] as string) : undefined,
          cosmeticId: typeof d["cosmeticId"] === "string" ? (d["cosmeticId"] as string) : undefined,
          cookies: typeof d["cookies"] === "string" ? (d["cookies"] as string) : undefined,
          cooldownMs: typeof d["cooldownMs"] === "number" && Number.isFinite(d["cooldownMs"] as number) ? (d["cooldownMs"] as number) : undefined,
          cooldownRemainingMs: typeof d["cooldownRemainingMs"] === "number" && Number.isFinite(d["cooldownRemainingMs"] as number) ? (d["cooldownRemainingMs"] as number) : undefined,
        } as unknown as never);
      }
      return;
    }
    if (t === "error") {
      const m = d["message"];
      const rid = typeof d["requestId"] === "string" && d["requestId"] !== "" ? (d["requestId"] as string) : undefined;
      this.emit({
        type: "error",
        message: typeof m === "string" && m !== "" ? m : "join_failed",
        requestId: rid,
        reason: typeof d["reason"] === "string" ? (d["reason"] as string) : undefined,
        abilityId: typeof d["abilityId"] === "string" ? (d["abilityId"] as string) : undefined,
        cooldownRemainingMs: typeof d["cooldownRemainingMs"] === "number" && Number.isFinite(d["cooldownRemainingMs"] as number) ? (d["cooldownRemainingMs"] as number) : undefined,
      } as unknown as never);
      return;
    }
    if (t === "snapshot" || t === "snapshot_lite") {
      const snap = normalizeSnapshot(d);
      if (snap) {
        if (snap.seq > 0) this.lastSeq = Math.max(this.lastSeq, snap.seq);
        else this.lastSeq = snap.seq;
        this.emit({ type: "snapshot", snapshot: snap });
      }
      return;
    }
    if (t === "click") {
      if (!this.trackSeq(d)) return;
      const cosRaw = d["cosmetics"];
      const cos = cosRaw && typeof cosRaw === "object" ? normalizeCosmetics(cosRaw as Record<string, unknown>) : undefined;
      this.emit({
        type: "click",
        seq: num(d["seq"], 0),
        userId: typeof d["userId"] === "string" ? (d["userId"] as string) : "",
        amount: str(d["amount"]),
        credits: str(d["credits"]),
        creditsGained: typeof d["creditsGained"] === "string" ? (d["creditsGained"] as string) : undefined,
        combo: typeof d["combo"] === "number" ? (d["combo"] as number) : num(d["combo"], 0),
        crit: d["crit"] === true,
        cps: typeof d["cps"] === "string" ? (d["cps"] as string) : undefined,
        contribution: typeof d["contribution"] === "string" ? (d["contribution"] as string) : undefined,
        totalClicks: typeof d["totalClicks"] === "string" ? (d["totalClicks"] as string) : typeof d["total_clicks"] === "string" ? (d["total_clicks"] as string) : undefined,
        xp: typeof d["xp"] === "string" ? (d["xp"] as string) : undefined,
        level: typeof d["level"] === "number" && Number.isFinite(d["level"]) ? (d["level"] as number) : undefined,
        nextLevelXp: typeof d["nextLevelXp"] === "string" ? (d["nextLevelXp"] as string) : typeof d["next_level_xp"] === "string" ? (d["next_level_xp"] as string) : undefined,
        clickPower: typeof d["clickPower"] === "string" ? (d["clickPower"] as string) : typeof d["click_power"] === "string" ? (d["click_power"] as string) : undefined,
        x: num(d["x"], 0.5),
        y: num(d["y"], 0.5),
        player: undefined,
        cosmetics: cos,
        bakeryCookies: typeof d["bakeryCookies"] === "string" ? (d["bakeryCookies"] as string) : undefined,
        bakeryCps: typeof d["bakeryCps"] === "string" ? (d["bakeryCps"] as string) : undefined,
        bakeryLevel: typeof d["bakeryLevel"] === "number" && Number.isFinite(d["bakeryLevel"]) ? (d["bakeryLevel"] as number) : undefined,
        bakeryNextLevelCookies: typeof d["bakeryNextLevelCookies"] === "string" ? (d["bakeryNextLevelCookies"] as string) : undefined,
        bakeryLevelProgress: typeof d["bakeryLevelProgress"] === "number" && Number.isFinite(d["bakeryLevelProgress"]) ? (d["bakeryLevelProgress"] as number) : undefined,
        bakeryLifetimeCookies: typeof d["bakeryLifetimeCookies"] === "string" ? (d["bakeryLifetimeCookies"] as string) : undefined,
      });
      return;
    }
    if (t === "player_joined") {
      if (!this.trackSeq(d)) return;
      const p =
        d["player"] && typeof d["player"] === "object"
          ? normalizePlayer(d["player"] as Record<string, unknown>)
          : normalizePlayer(d);
      this.emit({ type: "player_joined", seq: this.lastSeq, player: p });
      return;
    }
    if (t === "player_left") {
      if (!this.trackSeq(d)) return;
      const userId = typeof d["userId"] === "string" ? (d["userId"] as string) : "";
      this.emit({
        type: "player_left",
        seq: this.lastSeq,
        userId,
        name: typeof d["name"] === "string" ? (d["name"] as string) : userId,
      });
      return;
    }
    if (t === "event_started") {
      if (!this.trackSeq(d)) return;
      const ev =
        d["event"] && typeof d["event"] === "object"
          ? normalizeEvent(d["event"] as Record<string, unknown>)
          : normalizeEvent(d);
      this.emit({ type: "event_started", seq: this.lastSeq, event: ev });
      return;
    }
    if (t === "event_ended") {
      if (!this.trackSeq(d)) return;
      const id =
        typeof d["id"] === "string"
          ? (d["id"] as string)
          : typeof d["eventId"] === "string"
            ? (d["eventId"] as string)
            : "";
      const sid = typeof d["sneakyId"] === "string" ? (d["sneakyId"] as string) : typeof d["sneaky_id"] === "string" ? (d["sneaky_id"] as string) : undefined;
      const cid = typeof d["cookieId"] === "string" ? (d["cookieId"] as string) : typeof d["cookie_id"] === "string" ? (d["cookie_id"] as string) : undefined;
      this.emit({ type: "event_ended", seq: this.lastSeq, id, rewardCredits: str(d["rewardCredits"]), sneakyId: sid, cookieId: cid });
      return;
    }
    if (t === "presence") {
      if (!this.trackSeq(d)) return;
      const p = d["player"] && typeof d["player"] === "object" ? normalizePlayer(d["player"] as Record<string, unknown>) : normalizePlayer(d);
      this.emit({
        type: "presence",
        seq: this.lastSeq,
        player: p,
        bakeryCookies: typeof d["bakeryCookies"] === "string" ? (d["bakeryCookies"] as string) : undefined,
        bakeryCps: typeof d["bakeryCps"] === "string" ? (d["bakeryCps"] as string) : undefined,
        bakeryLevel: typeof d["bakeryLevel"] === "number" && Number.isFinite(d["bakeryLevel"]) ? (d["bakeryLevel"] as number) : undefined,
        bakeryNextLevelCookies: typeof d["bakeryNextLevelCookies"] === "string" ? (d["bakeryNextLevelCookies"] as string) : undefined,
        bakeryLevelProgress: typeof d["bakeryLevelProgress"] === "number" && Number.isFinite(d["bakeryLevelProgress"]) ? (d["bakeryLevelProgress"] as number) : undefined,
        bakeryLifetimeCookies: typeof d["bakeryLifetimeCookies"] === "string" ? (d["bakeryLifetimeCookies"] as string) : undefined,
      });
      return;
    }
    if (t === "upgrade_purchased") {
      handleUpgradePurchased(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "module_upgraded") {
      handleModuleUpgraded(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "project_completed") {
      handleProjectCompleted(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "project_started") {
      handleProjectStarted(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "spec_chosen") {
      if (!this.trackSeq(d)) return;
      this.emit({
        type: "spec_chosen",
        seq: num(d["seq"], this.lastSeq),
        userId: typeof d["userId"] === "string" ? (d["userId"] as string) : "",
        specId: typeof d["specId"] === "string" ? (d["specId"] as string) : "",
      } as unknown as never);
      return;
    }
    if (t === "ability_activated") {
      handleAbilityActivated(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "quest_claimed") {
      handleQuestClaimed(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "cosmetic_purchased") {
      handleCosmeticPurchased(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "cosmetic_equipped") {
      handleCosmeticEquipped(d, (e) => this.emit(e as never), (x) => this.trackSeq(x), this.lastSeq);
      return;
    }
    if (t === "sneaky_collected") {
      if (!this.trackSeq(d)) return;
      this.emit({
        type: "sneaky_collected",
        seq: num(d["seq"], this.lastSeq),
        userId: typeof d["userId"] === "string" ? (d["userId"] as string) : "",
        sneakyId: typeof d["sneakyId"] === "string" ? (d["sneakyId"] as string) : typeof d["sneaky_id"] === "string" ? (d["sneaky_id"] as string) : "",
        amount: str(d["amount"]),
        credits: str(d["credits"]),
        x: num(d["x"], 0.5),
        y: num(d["y"], 0.5),
      });
      return;
    }
    if (t === "mini_collected") {
      if (!this.trackSeq(d)) return;
      this.emit({
        type: "mini_collected",
        seq: num(d["seq"], this.lastSeq),
        userId: typeof d["userId"] === "string" ? (d["userId"] as string) : "",
        cookieId: typeof d["cookieId"] === "string" ? (d["cookieId"] as string) : typeof d["cookie_id"] === "string" ? (d["cookie_id"] as string) : "",
        amount: str(d["amount"]),
        credits: str(d["credits"]),
        multiplier: num(d["multiplier"], 2),
        x: num(d["x"], 0.5),
        y: num(d["y"], 0.5),
      });
      return;
    }
    if (t === "golden_redeemed") {
      if (!this.trackSeq(d)) return;
      this.emit({
        type: "golden_redeemed",
        seq: num(d["seq"], this.lastSeq),
        userId: typeof d["userId"] === "string" ? (d["userId"] as string) : "",
        amount: str(d["amount"]),
        credits: str(d["credits"]),
        combo: typeof d["combo"] === "number" ? (d["combo"] as number) : num(d["combo"], 0),
        crit: d["crit"] === true,
        x: num(d["x"], 0.5),
        y: num(d["y"], 0.5),
        frenzyEndsAt: num(d["frenzyEndsAt"], Date.now()),
      } as unknown as never);
      return;
    }
    if (t === "tester_cps") {
      if (!this.trackSeq(d)) return;
      this.emit({
        type: "tester_cps",
        seq: num(d["seq"], this.lastSeq),
        userId: typeof d["userId"] === "string" ? (d["userId"] as string) : "",
        cps: num(d["cps"], 0),
      } as unknown as never);
      return;
    }
  }

  private lastHelloAt = 0;
  private trackSeq(d: Record<string, unknown>): boolean {
    const seq = num(d["seq"], 0);
    if (seq > 0) {
      // Dedupe only strictly-older delivery. Equal-seq redelivery (ack and
      // its broadcast share one seq) must still apply: all purchase merges
      // are idempotent (max-level / absolute balances), so applying twice is
      // safe while dropping would lose the broadcast heal.
      if (seq < this.lastSeq) return false;
      if (this.lastSeq > 0 && hasSeqGap(this.lastSeq, seq)) {
        const now = Date.now();
        if (now - this.lastHelloAt > WS_RESYNC_THROTTLE_MS) {
          this.lastHelloAt = now;
          this.sendHello();
        }
      }
      this.lastSeq = Math.max(this.lastSeq, seq);
    }
    return true;
  }

  private sendHello() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(this.hello()));
      } catch {
        return;
      }
    }
  }

  private flushOutbox() {
    if (this.outbox.length === 0) return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const queued = this.outbox;
    this.outbox = [];
    for (const ev of queued) {
      try {
        this.ws.send(JSON.stringify(ev));
      } catch {
        // Re-queue the remainder so a mid-flush failure loses nothing; the
        // next open flushes again. Order is preserved (FIFO).
        this.outbox = [...queued.slice(queued.indexOf(ev)), ...this.outbox];
        return;
      }
    }
  }

  send(event: ClientEvent): void {
    if (event.type === "resync") {
      this.sendHello();
      return;
    }
    if (event.type === "hello") {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify(this.hello()));
        } catch {
          return;
        }
      }
      return;
    }
    if (event.type === "heartbeat") {
      // Heartbeats are ephemeral: never queue, just drop when offline.
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify(event));
        } catch {
          return;
        }
      }
      return;
    }
    // Fire-and-forget with background queue: queue when the socket is not
    // OPEN (reconnect replay flushes FIFO on open) instead of dropping.
    // Cap the outbox so a long offline stretch cannot grow memory unbounded;
    // clicks are ephemeral (drop oldest), buys are never dropped here — the
    // store replays pending buys by requestId on reconnect.
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(event));
        return;
      } catch {
        // fall through to queue
      }
    }
    if (event.type === "click") {
      if (this.outbox.length > WS_OUTBOX_CLICK_CAP) this.outbox.shift();
      this.outbox.push(event);
      return;
    }
    this.outbox.push(event);
    if (this.outbox.length > WS_OUTBOX_MAX) this.outbox.splice(0, this.outbox.length - WS_OUTBOX_MAX);
  }

  disconnect(): void {
    this.closed = true;
    if (this.timer) window.clearTimeout(this.timer);
    this.stopHeartbeat();
    try {
      this.ws?.close();
    } catch {
      return;
    }
    this.ws = null;
  }
}
